import { CompanyTitledPage } from "../../TitledPage";
import SupplyTable from "./component/supplyTable";
import DateInput from "../../../components/DateInput";
import SupplierGroupSelect from "./component/supplierGroupSelect";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import useSupplyListForm from "./hook/useSupplyListForm";
import AddProductDialog from "./component/AddProductDialog";
import AddProductDetailMasterDialog from "./component/AddProductDetailMasterDialog";
import AddProductMasterDialog from "./component/AddProductMasterDialog";
import PrintPreviewDialog from "./component/PrintPreviewDialog";
import { getHyphenedDate } from "../../../utils/utils";
import ErrorMessageBox from "../../../components/ErrorMessageBox";
import SupplyTablePaging from "./component/SupplyTablePaging";
import { useSupplyFormKeyHandler } from "./hook/useSupplyFormKeyHandler";
import ProductHistoryModal from "./component/Obsoluted/ProductHistoryModal/ProductHistoryModal";
import RightSidebar from "./component/RightSidebar/RightSidebar";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  PrevPageDropZone,
  NextPageDropZone,
  CancelDropZone,
} from "./component/PageDropZones";
import DragAutoScroll from "./component/DragAutoScroll";
import SupplyDivisionSelect from "./component/SupplyDivisionSelect";

function SupplyListPage() {
  const supplyListForm = useSupplyListForm();

  const keyHandler = useSupplyFormKeyHandler(
    supplyListForm.page,
    supplyListForm.maxPage,
    supplyListForm.movePage,
  );

  const handleClickSubmit = async (e) => {
    await supplyListForm.submit();
  };

  const handleClickSendFax = async (e) => {
    await supplyListForm.openPreviewDialog();
  };

  const handleChangeEnabledFilteringWithSupply = (e) => {
    supplyListForm.setEnabledFilteringWithSupply(e.target.checked);
  };

  const handleChangeEnabledDisplayRemarks = (e) => {
    supplyListForm.setEnabledDisplayRemarks(e.target.checked);
  };

  const handleDebugState = () => {
    const snapshot = {
      deliveryDate: supplyListForm.deliveryDate,
      supplierGroupSelectValue: supplyListForm.supplierGroupSelectValue,
      supplyDivisionSelectValue: supplyListForm.supplyDivisionSelectValue,
      tabPos: supplyListForm.tabPos,
      page: supplyListForm.page,
      maxPage: supplyListForm.maxPage,
      enabledFilteringWithSupply: supplyListForm.enabledFilteringWithSupply,
      enabledDisplayRemarks: supplyListForm.enabledDisplayRemarks,
      targetSupplier: supplyListForm.targetSupplier,
      supplierGroups: supplyListForm.supplierGroups,
      supplyDivisions: supplyListForm.supplyDivisions,
      errorMessages: supplyListForm.errorMessages,
      supplyFormForms: supplyListForm.supplyForm?.forms,
      pageSupplyForm: supplyListForm.pageSupplyForm,
      supplyHistoryAll: supplyListForm.supplyHistoryAll,
      historyHidden: supplyListForm.productHistoryModal?.hidden,
      rightSidebar: {
        open: supplyListForm.rightSidebarOpen,
        mode: supplyListForm.rightSidebarMode,
      },
    };
    // eslint-disable-next-line no-console
    console.log("[SupplyListPage Debug State]", snapshot);
  };

  const disabledConfirmButton =
    supplyListForm.supplyForm.forms.length == 0 ||
    supplyListForm.errorMessages.length > 0 ||
    supplyListForm.isProceeding;

  const disabledSendButtons =
    !supplyListForm.hasProductWithSupply() ||
    supplyListForm.deliveryDate < getHyphenedDate() ||
    supplyListForm.errorMessages.length > 0 ||
    supplyListForm.isProceeding;

  return (
    <DndProvider backend={HTML5Backend}>
      <DragAutoScroll />
      <CompanyTitledPage title="発注一覧">
        <Box>
          <Grid container spacing={2}>
            <Grid item>
              入荷日:
              <DateInput
                value={supplyListForm.deliveryDate}
                setValue={supplyListForm.changeDeliveryDate}
              />
            </Grid>
            <Grid item xs>
              仕入先:
              <SupplierGroupSelect
                supplierGroups={supplyListForm.supplierGroups}
                supplierGroupValue={supplyListForm.supplierGroupSelectValue}
                changeSupplierGroupSelect={
                  supplyListForm.changeSupplierGroupSelect
                }
              />
            </Grid>
            <Grid item>
              <Checkbox
                checked={supplyListForm.enabledFilteringWithSupply}
                onChange={handleChangeEnabledFilteringWithSupply}
              />
              発注数量入力済みで絞る
            </Grid>
            <Grid item>
              <Checkbox
                checked={supplyListForm.enabledDisplayRemarks}
                onChange={handleChangeEnabledDisplayRemarks}
              />
              備考を表示する
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                // disabled={disabledSendButtons} ※メール送信未実装のため
                disabled
              >
                発注書メール送信
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={handleClickSendFax}
                disabled={disabledSendButtons}
              >
                発注書PDF作成
              </Button>
            </Grid>
          </Grid>

          <ErrorMessageBox messages={supplyListForm.errorMessages} />
          <Grid container spacing={2} mt={1} mb={2} alignItems="center">
            <Grid item>
              <Typography>発注区分</Typography>
            </Grid>
            <Grid item xs>
              <SupplyDivisionSelect
                supplyDivisions={supplyListForm.supplyDivisions}
                supplyDivisionValue={supplyListForm.supplyDivisionSelectValue}
                changeSupplyDivision={supplyListForm.changeSupplyDivisionSelect}
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={handleClickSubmit}
                disabled={disabledConfirmButton}
              >
                確定
              </Button>
            </Grid>
          </Grid>

          {/* Page change drop zones (fixed position) */}
          <PrevPageDropZone
            currentPage={supplyListForm.page}
            onHoverPreviousPage={supplyListForm.navigateToPreviousPage}
          />
          <NextPageDropZone
            currentPage={supplyListForm.page}
            maxPage={supplyListForm.maxPage}
            onHoverNextPage={supplyListForm.navigateToNextPage}
          />
          <CancelDropZone />

          {/* Table container */}
          <Box sx={{ overflow: "hidden" }}>
            <SupplyTable
              supplyForm={supplyListForm.pageSupplyForm}
              suppliers={
                supplyListForm.targetSupplier
                  ? [supplyListForm.targetSupplier]
                  : []
              }
              enabledDisplayRemarks={supplyListForm.enabledDisplayRemarks}
              keyHandler={keyHandler}
              onHideProduct={supplyListForm.productHistoryModal.handleHide}
              onShowProduct={supplyListForm.productHistoryModal.handleBack}
              onShowProductAt={supplyListForm.productHistoryModal.handleBackAt}
              onAddProduct={supplyListForm.handleAddProductFromSidebar}
              onAddProductAt={supplyListForm.handleAddProductFromSidebarAt}
              currentPage={supplyListForm.page}
              onChangePage={supplyListForm.movePage}
            />
            <SupplyTablePaging
              maxPage={supplyListForm.maxPage}
              page={supplyListForm.page}
              changePage={supplyListForm.movePage}
            />
          </Box>

          {/* Combined Right Sidebar */}
          <RightSidebar
            open={supplyListForm.rightSidebarOpen}
            activeMode={supplyListForm.rightSidebarMode}
            onToggle={supplyListForm.toggleRightSidebar}
            onSwitchMode={supplyListForm.switchRightSidebarMode}
            onClose={supplyListForm.closeSidebar}
            onHiddenRowDragBegin={supplyListForm.closeSidebar}
            onHiddenRowDragCancel={supplyListForm.openSidebar}
            // AddProduct props
            onAddProduct={supplyListForm.handleAddProductFromSidebar}
            tabPosition={supplyListForm.tabPos}
            deliveryDate={supplyListForm.deliveryDate}
            supplierGroupValue={supplyListForm.supplierGroupSelectValue}
            supplyDivisionValue={supplyListForm.supplyDivisionSelectValue}
            submitVersion={supplyListForm.submitVersion}
            addedProductList={supplyListForm.supplyForm.forms.map((p) => ({
              ...p,
              priceDetailTypeId: p.supplyTypeId,
            }))}
            predictAddedProduct={(prod, addedProd) => {
              // データがない場合(null/undefined/0/"")は比較をスキップ
              const isEmpty = (v) => v == null || v === 0 || v === "" || v === "0";
              const eq = (a, b) => {
                if (isEmpty(a) && isEmpty(b)) return true;  // 両方空 → 一致
                if (isEmpty(a) || isEmpty(b)) return false;  // 片方だけ空 → 不一致
                // eslint-disable-next-line eqeqeq
                return a == b;
              };
              return (
                eq(prod.productId, addedProd.productId) &&
                eq(prod.originId, addedProd.originId) &&
                eq(prod.rankId, addedProd.rankId) &&
                eq(prod.productClassId, addedProd.productClassId) &&
                eq(prod.packingId, addedProd.packingId) &&
                eq(prod.weight, addedProd.weight) &&
                eq(prod.weightUnitId, addedProd.weightUnitId) &&
                eq(prod.otherSpec, addedProd.otherSpec) &&
                !addedProd.isDelete
              );
            }}
            openAddProductMasterDialog={
              supplyListForm.handleOpenAddProductMasterFromSidebar
            }
            openAddProductDetailMasterDialog={
              supplyListForm.handleOpenAddProductDetailMasterFromSidebar
            }
            sidebarRefreshTrigger={supplyListForm.sidebarRefreshTrigger}
            sidebarScrollToProductId={supplyListForm.sidebarScrollToProductId}
            onSidebarScrollComplete={() =>
              supplyListForm.setSidebarScrollToProductId(null)
            }
            // History props
            supplyHistoryAll={supplyListForm.supplyHistoryAll}
            hidden={supplyListForm.productHistoryModal.hidden}
            selected={supplyListForm.productHistoryModal.selected}
            toggleSelect={supplyListForm.productHistoryModal.toggleSelect}
            handleBack={supplyListForm.productHistoryModal.handleBack}
            handleBulkBack={supplyListForm.productHistoryModal.handleBulkBack}
            handleToggleAllHidden={
              supplyListForm.productHistoryModal.handleToggleAllHidden
            }
            handleHide={supplyListForm.productHistoryModal.handleHide}
            moveHiddenItem={supplyListForm.productHistoryModal.moveHiddenItem}
          />
        </Box>

        <AddProductDialog
          isOpen={supplyListForm.addProductDialog.isOpen}
          onClose={supplyListForm.addProductDialog.onClose}
          addingProduct={supplyListForm.addingProduct}
          addedProducts={supplyListForm.supplyForm.forms}
          tabPosition={supplyListForm.tabPos}
        />

        <AddProductMasterDialog
          isOpen={supplyListForm.addProductMasterDialog.isOpen}
          onClose={supplyListForm.addProductMasterDialog.onClose}
          addingProduct={supplyListForm.addingProduct}
        />

        <AddProductDetailMasterDialog
          isOpen={supplyListForm.addProductDetailMasterDialog.isOpen}
          onClose={supplyListForm.addProductDetailMasterDialog.onClose}
          addingProduct={supplyListForm.addingProduct}
        />

        <PrintPreviewDialog
          isOpen={supplyListForm.printPreviewDialog.isOpen}
          onClose={supplyListForm.closePreviewDialog}
          supplyForms={supplyListForm.supplyForm.forms}
          deliveryDate={supplyListForm.deliveryDate}
          targetSupplier={supplyListForm.targetSupplier}
          supplyDivisionId={parseInt(supplyListForm.supplyDivisionSelectValue)}
        />
        <ProductHistoryModal {...supplyListForm.productHistoryModal} />
      </CompanyTitledPage>
    </DndProvider>
  );
}

export default SupplyListPage;
